import type { Dispatch, SetStateAction, RefObject } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  HStack,
  Text,
  Icon,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  CircularProgress,
  useColorMode,
} from '@chakra-ui/react'
import {
  Lock,
  Check,
  Copy,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash2,
  FileEdit,
  Info,
  Flame,
  File,
} from 'lucide-react'
import { formatTimeRemaining } from '@core/utils/format'
import { isExpiringSoon, getExpiryProgress, getExpiryColorScheme } from '@/lib/utils/share-url'
import { type ShareSummary } from '@/lib/api/keys'

interface ShareItemProps {
  share: ShareSummary
  depth: number
  selectedFileId?: string
  onSelectFile: (shareId: string) => void
  onCloneShare?: (shareId: string) => void
  getDisplayName: (share: ShareSummary) => string
  isEncrypted: (share: ShareSummary) => boolean
  getShareUrl: (share: ShareSummary, mode?: 'edit') => string
  handleCopyLink: (share: ShareSummary) => void
  handleRevoke: (shareId: string) => void
  handleViewDetails: (share: ShareSummary) => void
  isCopied: (shareId: string) => boolean
  draggingShareId: string | null
  setDraggingShareId: Dispatch<SetStateAction<string | null>>
  dragImageRef: RefObject<HTMLDivElement | null>
  handleDragEnd: () => void
}

export function ShareItem({
  share,
  depth,
  selectedFileId,
  onSelectFile,
  onCloneShare,
  getDisplayName,
  isEncrypted,
  getShareUrl,
  handleCopyLink,
  handleRevoke,
  handleViewDetails,
  isCopied,
  draggingShareId,
  setDraggingShareId,
  dragImageRef,
  handleDragEnd,
}: ShareItemProps) {
  const { colorMode } = useColorMode()

  return (
    <HStack
      pr={2}
      py={1.5}
      pl={`${(2 + depth * 4) * 4}px`}
      borderRadius="md"
      cursor="pointer"
      onClick={() => onSelectFile(share.id)}
      bg={
        selectedFileId === share.id
          ? colorMode === 'dark'
            ? 'stone.700'
            : 'stone.200'
          : 'transparent'
      }
      _hover={{
        bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
      }}
      transition="all 0.15s ease"
      role="group"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(
          'text/plain',
          JSON.stringify({
            type: 'share',
            id: share.id,
            sourceFolder: share.folderPath || '/',
          }),
        )
        e.dataTransfer.effectAllowed = 'move'
        if (dragImageRef.current) {
          e.dataTransfer.setDragImage(dragImageRef.current, 0, 0)
        }
        setDraggingShareId(share.id)
      }}
      onDragEnd={handleDragEnd}
      opacity={draggingShareId === share.id ? 0.4 : 1}
      transform={draggingShareId === share.id ? 'scale(0.97)' : undefined}
    >
      <Icon
        as={File}
        boxSize={4}
        color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
        flexShrink={0}
      />
      <Tooltip label={getDisplayName(share)} placement="right" openDelay={500}>
        <Text
          flex={1}
          fontSize="sm"
          noOfLines={1}
          color={colorMode === 'dark' ? 'stone.200' : 'stone.700'}
        >
          {getDisplayName(share)}
        </Text>
      </Tooltip>
      {isEncrypted(share) && (
        <Tooltip label="Protected">
          <Icon
            as={Lock}
            boxSize={3}
            color={colorMode === 'dark' ? 'stone.500' : 'stone.400'}
            flexShrink={0}
          />
        </Tooltip>
      )}

      {share.burnAfterRead && share.status === 'active' && (
        <Tooltip label="Burns after reading">
          <Icon as={Flame} boxSize={3} color="orange.400" flexShrink={0} />
        </Tooltip>
      )}

      {isExpiringSoon(share.expiresAt) && (
        <Tooltip
          label={`Expires in ${formatTimeRemaining(share.expiresAt)}`}
        >
          <CircularProgress
            value={getExpiryProgress(share.expiresAt)}
            size="14px"
            thickness="12px"
            color={getExpiryColorScheme(getExpiryProgress(share.expiresAt))}
            trackColor={colorMode === 'dark' ? 'stone.600' : 'stone.200'}
          />
        </Tooltip>
      )}

      <Menu>
        <MenuButton
          as={IconButton}
          aria-label="Actions"
          icon={<Icon as={MoreVertical} boxSize={4} />}
          variant="ghost"
          size="xs"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
        />
        <MenuList minW="150px" onClick={(e) => e.stopPropagation()}>
          <MenuItem
            icon={<Icon as={ExternalLink} boxSize={4} />}
            as="a"
            href={getShareUrl(share)}
            target="_blank"
          >
            Preview share
          </MenuItem>
          <MenuItem
            icon={
              <Icon as={isCopied(share.id) ? Check : Copy} boxSize={4} />
            }
            onClick={() => handleCopyLink(share)}
          >
            {isCopied(share.id) ? 'Copied!' : 'Copy link'}
          </MenuItem>
          {share.isModifiable && share.status === 'active' && (
            <MenuItem
              icon={<Icon as={Edit} boxSize={4} />}
              as={RouterLink}
              to={getShareUrl(share, 'edit')}
            >
              Edit
            </MenuItem>
          )}
          <MenuItem
            icon={<Icon as={Info} boxSize={4} />}
            onClick={() => handleViewDetails(share)}
          >
            View Details
          </MenuItem>
          {onCloneShare && share.status === 'active' && (
            <MenuItem
              icon={<Icon as={FileEdit} boxSize={4} />}
              onClick={() => onCloneShare(share.id)}
            >
              Clone and Edit
            </MenuItem>
          )}
          <MenuDivider />
          <MenuItem
            icon={<Icon as={Trash2} boxSize={4} />}
            color="red.500"
            onClick={() => handleRevoke(share.id)}
          >
            Delete
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  )
}
